import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, PenSquare, Users } from "lucide-react";
import { GroupCreate } from "./GroupSheets";
import { api } from "@/lib/api";
import { useTalk, TkAvatar, tkErr } from "./TalkLayout";

export const ago = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function UserSearch({ onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (!q.trim()) { setRows([]); return; }
    const t = setTimeout(() => api.get("/talk/users", { params: { q } }).then((r) => setRows(r.data.users)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const open = async (u) => {
    try { const { data } = await api.post("/talk/chats", { user_id: u.user_id }); nav(`/talk/c/${data.chat.id}`); } catch (e) { tkErr(e); }
  };
  return (
    <div className="rounded-3xl border border-[#FF2BD6]/30 bg-[#FF2BD6]/5 p-4 mb-5" data-testid="talk-user-search">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-white/50" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username or name"
          className="flex-1 bg-transparent outline-none text-base" data-testid="talk-user-search-input" />
        <button onClick={onClose} data-testid="talk-user-search-close"><X className="w-4 h-4 text-white/50" /></button>
      </div>
      <div className="mt-3 space-y-1">
        {rows.map((u) => (
          <button key={u.user_id} onClick={() => open(u)} data-testid={`talk-user-result-${u.username}`}
            className="w-full flex items-center gap-3 p-2 rounded-2xl hover:bg-white/5 text-left transition-colors">
            <TkAvatar p={u} size={40} />
            <div className="min-w-0"><div className="font-bold truncate">{u.display_name}</div><div className="text-xs text-white/50 mono">@{u.username}</div></div>
          </button>
        ))}
        {q && !rows.length && <div className="text-xs text-white/40 py-2">No members match “{q}”.</div>}
      </div>
    </div>
  );
}

export default function TalkChats() {
  const { me, subscribe } = useTalk();
  const [chats, setChats] = useState(null);
  const [searching, setSearching] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const load = () => api.get("/talk/chats").then((r) => setChats(r.data.chats)).catch(() => setChats([]));
  useEffect(() => { load(); }, []);
  useEffect(() => subscribe((t) => { if (["message", "presence", "chat_update"].includes(t)) load(); }), [subscribe]);
  return (
    <div data-testid="talk-chats">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/40">@{me.profile.username}</div>
          <h1 className="font-heading font-black text-4xl">Chats</h1>
        </div>
        <div className="flex gap-2">
        <button onClick={() => setGrouping(true)} data-testid="talk-new-group-btn" aria-label="New group"
          className="flex items-center gap-2 border border-white/15 hover:bg-white/5 transition-colors font-black px-4 py-2.5 rounded-full text-sm">
          <Users className="w-4 h-4" /> Group
        </button>
        <button onClick={() => setSearching(true)} data-testid="talk-new-chat-btn"
          className="flex items-center gap-2 bg-[#FF2BD6] hover:bg-[#ff5ce0] transition-colors text-black font-black px-4 py-2.5 rounded-full text-sm">
          <PenSquare className="w-4 h-4" /> New chat
        </button>
        </div>
      </div>
      {searching && <UserSearch onClose={() => setSearching(false)} />}
      {grouping && <GroupCreate onClose={() => setGrouping(false)} />}
      {chats === null ? <div className="text-white/40 text-sm animate-pulse">Loading…</div> : !chats.length ? (
        <div className="py-14 text-center" data-testid="talk-chats-empty">
          <div className="font-heading font-black text-2xl">No chats yet</div>
          <p className="text-white/50 text-sm mt-1">Find a friend by their username to start messaging.</p>
          <button onClick={() => setSearching(true)} className="mt-5 border border-white/15 rounded-full px-5 py-2 font-bold hover:bg-white/5" data-testid="talk-empty-search-btn">Find people</button>
        </div>
      ) : (
        <div className="space-y-1">
          {chats.map((c) => { const who = c.type === "group" ? { display_name: c.name, avatar_url: c.avatar_url } : c.other; return (
            <Link key={c.id} to={`/talk/c/${c.id}`} data-testid={`talk-chat-row-${c.id}`}
              className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white/[0.04] transition-colors">
              <TkAvatar p={who} size={50} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold truncate">{who.display_name}</span>
                  {c.type === "group" && <span className="text-[10px] mono text-white/40 flex items-center gap-0.5"><Users className="w-3 h-3" />{c.members.length}</span>}
                  {c.call && <span className="text-[10px] font-black bg-[#39FF14] text-black rounded-full px-1.5" data-testid={`talk-chat-live-${c.id}`}>LIVE CALL</span>}
                  <span className="ml-auto text-[11px] text-white/40 mono">{ago(c.last_message?.at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate ${c.unread ? "text-white font-semibold" : "text-white/50"}`}>
                    {c.last_message?.sender_id === me.profile.user_id ? "You: " : ""}{c.last_message?.text}
                  </span>
                  {c.unread > 0 && <span className="ml-auto bg-[#FF2BD6] text-black text-[10px] font-black rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" data-testid={`talk-chat-unread-${c.id}`}>{c.unread}</span>}
                </div>
              </div>
            </Link>
          ); })}
        </div>
      )}
    </div>
  );
}
